/* ==========================================================================
   MAISON NOIR & CO — Live palette switcher (DEV / PREVIEW ONLY)
   Applies a palette by overriding the --mn-* tokens on :root. The whole store
   reads those tokens, so it re-skins instantly. Choice persists in
   localStorage so it survives hot-reloads while you compare.
   ========================================================================== */
(function () {
  'use strict';

  // ---- Palette data (curated). Each defines the 3 raw families + channels. ----
  // For light palettes: cream = bg, espresso = text. For dark: it flips
  // (cream is the dark bg, espresso is the light text) — the token mapping
  // below is identical because the semantic tokens already reference these.
  var PALETTES = [
    { id: 'cream-brass', name: 'Cream & Brass', mood: 'Warm bone + antique brass', mode: 'light',
      t: { cream:'#F7F4EC', cream2:'#F1ECE0', cream3:'#E8E1D2', sand:'#D8CDB8', espresso:'#2A241C', espresso2:'#5C5344', accent:'#8A6A2F', accent2:'#B08D4A', clay:'#A9713F', creamRgb:'247 244 236', espressoRgb:'42 36 28', accentRgb:'138 106 47' } },
    { id: 'oat-forest', name: 'Oatmeal & Forest', mood: 'Soft white + deep forest green', mode: 'light',
      t: { cream:'#F6F5F0', cream2:'#EEEDE5', cream3:'#E3E2D6', sand:'#CFCDBB', espresso:'#232620', espresso2:'#535A4C', accent:'#2E4A34', accent2:'#436B4C', clay:'#9A6B3E', creamRgb:'246 245 240', espressoRgb:'35 38 32', accentRgb:'46 74 52' } },
    { id: 'sand-terracotta', name: 'Sand & Terracotta', mood: 'Pale sand + cognac clay', mode: 'light',
      t: { cream:'#F6EFE4', cream2:'#F0E6D6', cream3:'#E7DAC5', sand:'#D6C3A6', espresso:'#2E2620', espresso2:'#61533F', accent:'#A2532C', accent2:'#C0703F', clay:'#7C4A2E', creamRgb:'246 239 228', espressoRgb:'46 38 32', accentRgb:'162 83 44' } },
    { id: 'stone-sage', name: 'Stone & Sage', mood: 'Greige stone + muted sage', mode: 'light',
      t: { cream:'#F3F2EE', cream2:'#EAEAE3', cream3:'#DEDED5', sand:'#C7C6B8', espresso:'#282A26', espresso2:'#565A53', accent:'#54614F', accent2:'#71805F', clay:'#9C8567', creamRgb:'243 242 238', espressoRgb:'40 42 38', accentRgb:'84 97 79' } },
    // Ivory leans ROSE, not yellow: a blush-ivory ground (still neutral, no
    // pastel) so burgundy reads as the same family instead of fighting a
    // warm-yellow cream that looked aged.
    { id: 'ivory-burgundy', name: 'Ivory & Burgundy', mood: 'Blush ivory + oxblood wine', mode: 'light',
      t: { cream:'#F7ECE9', cream2:'#F1E3DF', cream3:'#E9D7D2', sand:'#D6BFB8', espresso:'#2A211E', espresso2:'#5C4B45', accent:'#7A2733', accent2:'#9C3B48', clay:'#9E6B45', creamRgb:'247 236 233', espressoRgb:'42 33 30', accentRgb:'122 39 51' } },
    { id: 'espresso-brass', name: 'Espresso & Brass', mood: 'Dark near-black + warm brass', mode: 'dark',
      t: { cream:'#1A1712', cream2:'#231F18', cream3:'#2E2820', sand:'#4A4234', espresso:'#F3EEE3', espresso2:'#B7AF9F', accent:'#C9A25C', accent2:'#E0C088', clay:'#B87A46', creamRgb:'26 23 18', espressoRgb:'243 238 227', accentRgb:'201 162 92' } }
  ];

  var STORE_KEY = 'mn-palette-choice';

  // Every --mn-* custom property the palette controls, mapped to a value.
  // Includes the RAW families AND the SEMANTIC tokens, so sections that read
  // either kind update. We set them directly on documentElement via
  // setProperty (highest reliability, beats any :root stylesheet).
  function varsFor(p) {
    var t = p.t;
    return {
      // raw families
      '--mn-cream': t.cream, '--mn-cream-2': t.cream2, '--mn-cream-3': t.cream3,
      '--mn-sand': t.sand,
      '--mn-espresso': t.espresso, '--mn-espresso-2': t.espresso2,
      '--mn-accent': t.accent, '--mn-accent-2': t.accent2, '--mn-clay': t.clay,
      '--mn-ink': t.espresso, '--mn-ink-2': t.espresso2,
      // rgb channels
      '--mn-cream-rgb': t.creamRgb, '--mn-espresso-rgb': t.espressoRgb,
      '--mn-ink-rgb': t.espressoRgb, '--mn-bone-rgb': t.creamRgb,
      '--mn-accent-rgb': t.accentRgb, '--mn-clay-rgb': t.accentRgb,
      // legacy alias
      '--mn-bone': t.espresso,
      // semantic tokens (re-point them explicitly, don't rely on cascade)
      '--mn-bg': t.cream, '--mn-surface': t.cream2, '--mn-surface-2': t.cream3,
      '--mn-text': t.espresso,
      '--mn-text-muted': 'rgb(' + t.espressoRgb + ' / 0.68)',
      '--mn-text-subtle': 'rgb(' + t.espressoRgb + ' / 0.48)',
      '--mn-hairline': 'rgb(' + t.espressoRgb + ' / 0.12)',
      '--mn-hairline-strong': 'rgb(' + t.espressoRgb + ' / 0.22)',
      '--mn-highlight': t.accent, '--mn-highlight-2': t.accent2,
      '--mn-brass-rule': 'rgb(' + t.accentRgb + ' / 0.85)'
    };
  }

  var APPLIED_KEYS = null; // remember which props we set, so reset can clear them

  function apply(p) {
    var vars = varsFor(p);
    var root = document.documentElement;
    APPLIED_KEYS = Object.keys(vars);
    APPLIED_KEYS.forEach(function (k) {
      // setProperty with 'important' beats any :root {} in stylesheets
      root.style.setProperty(k, vars[k], 'important');
    });
    // Tag the doc so a section could react to dark palettes if needed
    root.setAttribute('data-mn-palette', p.id);
    root.setAttribute('data-mn-palette-mode', p.mode);
    try { localStorage.setItem(STORE_KEY, p.id); } catch (e) {}
    markActive(p.id);
    var cur = document.querySelector('[data-mn-ps-current]');
    if (cur) cur.textContent = p.name + ' · ' + p.mode;
  }

  function reset() {
    var root = document.documentElement;
    if (APPLIED_KEYS) {
      APPLIED_KEYS.forEach(function (k) { root.style.removeProperty(k); });
    }
    root.removeAttribute('data-mn-palette');
    root.removeAttribute('data-mn-palette-mode');
    try { localStorage.removeItem(STORE_KEY); } catch (e) {}
    markActive(null);
    var cur = document.querySelector('[data-mn-ps-current]');
    if (cur) cur.textContent = 'theme default';
  }

  function markActive(id) {
    var btns = document.querySelectorAll('.mn-ps__opt');
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('aria-pressed', btns[i].dataset.id === id ? 'true' : 'false');
    }
  }

  // Only reveal in a development/preview environment, never on the live shop.
  function isPreviewEnv(root) {
    try {
      var host = location.hostname;
      if (host === '127.0.0.1' || host === 'localhost') return true;
      var qs = location.search || '';
      if (qs.indexOf('preview_theme_id') !== -1) return true;
      if (qs.indexOf('_fd=') !== -1 || qs.indexOf('_ab=') !== -1) return true; // theme editor frames
      // Shopify theme editor loads the storefront inside an iframe on admin
      if (window.self !== window.top && document.referrer.indexOf('/admin') !== -1) return true;
      // Liquid told us (design mode / preview_theme_id in content_for_header)
      if (root && root.getAttribute('data-mn-liquid-preview') === 'true') return true;
    } catch (e) {}
    return false;
  }

  function build() {
    var root = document.querySelector('[data-mn-palette-switcher]');
    if (!root) return;
    if (!isPreviewEnv(root)) return; // stay hidden on the live storefront
    root.hidden = false;

    var toggle = root.querySelector('.mn-ps__toggle');
    var panel = root.querySelector('.mn-ps__panel');
    toggle.addEventListener('click', function () {
      var open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
    });

    var list = root.querySelector('[data-mn-ps-list]');
    PALETTES.forEach(function (p) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'mn-ps__opt';
      b.dataset.id = p.id;
      b.setAttribute('aria-pressed', 'false');
      b.innerHTML =
        '<span class="mn-ps__swatches">' +
          '<span class="mn-ps__sw" style="background:' + p.t.cream + '"></span>' +
          '<span class="mn-ps__sw" style="background:' + p.t.accent + '"></span>' +
          '<span class="mn-ps__sw" style="background:' + p.t.espresso + '"></span>' +
        '</span>' +
        '<span class="mn-ps__opt-text">' +
          '<span class="mn-ps__opt-name">' + p.name + '</span>' +
          '<span class="mn-ps__opt-mood">' + p.mood + '</span>' +
        '</span>';
      b.addEventListener('click', function () { apply(p); });
      list.appendChild(b);
    });

    var resetBtn = root.querySelector('[data-mn-ps-reset]');
    if (resetBtn) resetBtn.addEventListener('click', reset);

    // Restore last choice
    var saved = null;
    try { saved = localStorage.getItem(STORE_KEY); } catch (e) {}
    if (saved) {
      var found = PALETTES.filter(function (p) { return p.id === saved; })[0];
      if (found) { apply(found); panel.hidden = false; toggle.setAttribute('aria-expanded', 'true'); }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
